import React, { useState, useMemo, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { useApp, UNITS, metersToUnit, unitToMeters } from '../../context/AppContext';
import { Copy, Grid3X3, Circle, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const MODE_LINEAR = 'linear';
const MODE_GRID = 'grid';
const MODE_DOME = 'dome';

const MultiProjectionPanel = () => {
  const { projectors, selectedObjectId, addProjector, updateProjector, projectSettings, setProjectors, setSelectedObjectId, saveToHistoryImmediate } = useApp();
  const unit = projectSettings?.unit || 'm';
  const unitCfg = UNITS[unit] || UNITS['m'];
  const decimals = unitCfg.decimals ?? 2;
  
  const [mode, setMode] = useState(MODE_LINEAR);
  const [count, setCount] = useState(3);
  const [direction, setDirection] = useState('right');
  const [overlapMode, setOverlapMode] = useState('percent'); // 'percent' or 'distance'
  const [overlapH, setOverlapH] = useState(15);
  const [overlapV, setOverlapV] = useState(15);
  const [overlapDistH, setOverlapDistH] = useState(1); // in current unit
  const [overlapDistV, setOverlapDistV] = useState(0.5);
  const [gridRows, setGridRows] = useState(2);
  const [gridCols, setGridCols] = useState(3);
  const [domeAngle, setDomeAngle] = useState(360);
  const [domeCount, setDomeCount] = useState(6);
  const [domeRadius, setDomeRadius] = useState(8);
  const [generatedIds, setGeneratedIds] = useState([]);

  const baseProjector = useMemo(() => {
    return projectors.find(p => p.id === selectedObjectId);
  }, [projectors, selectedObjectId]);

  const calcImageSize = useCallback((proj) => {
    if (!proj) return { w: 4, h: 2.5 };
    const throwDist = proj.throw_distance || 5;
    const throwRatio = proj.throw_ratio || 1.5;
    const w = throwDist / throwRatio;
    const ar = proj.aspect_ratio || '16:10';
    const [aw, ah] = ar.split(':').map(Number);
    const h = w * (ah / aw);
    return { w, h };
  }, []);

  // Calculate actual overlap in meters
  const getOverlapMeters = useCallback((imgW, imgH) => {
    if (overlapMode === 'percent') {
      // In linear mode, the single overlapH slider applies to the active direction
      return { h: imgW * overlapH / 100, v: imgH * (mode === MODE_LINEAR ? overlapH : overlapV) / 100 };
    } else {
      // In linear mode, the single distance input applies to the active direction
      const distH = unitToMeters(overlapDistH, unit);
      const distV = mode === MODE_LINEAR ? distH : unitToMeters(overlapDistV, unit);
      return { h: distH, v: distV };
    }
  }, [overlapMode, overlapH, overlapV, overlapDistH, overlapDistV, unit, mode]);

  // Get projector's local right and up vectors from rotation
  const getProjectorAxes = useCallback((proj) => {
    if (!proj) return { right: { x: 1, y: 0, z: 0 }, up: { x: 0, y: 1, z: 0 } };
    const rotY = (proj.rotation?.y || 0) * Math.PI / 180;
    // Projector faces forward along its local -Z after rotation
    // Right vector is perpendicular to forward in the XZ plane
    return {
      right: { x: Math.cos(rotY), y: 0, z: -Math.sin(rotY) },
      up: { x: 0, y: 1, z: 0 }
    };
  }, []);

  const stats = useMemo(() => {
    if (!baseProjector) return null;
    const img = calcImageSize(baseProjector);
    const overlap = getOverlapMeters(img.w, img.h);
    const spacingH = img.w - overlap.h;
    const spacingV = img.h - overlap.v;
    const resW = baseProjector.resolution_w || baseProjector.spec?.resolution_width || 1920;
    const resH = baseProjector.resolution_h || baseProjector.spec?.resolution_height || 1200;
    const overlapHPct = Math.round(overlap.h / img.w * 100);
    const overlapVPct = Math.round(overlap.v / img.h * 100);

    if (mode === MODE_LINEAR) {
      const isH = direction === 'left' || direction === 'right';
      const totalW = isH ? img.w + spacingH * (count - 1) : img.w;
      const totalH = isH ? img.h : img.h + spacingV * (count - 1);
      return {
        totalW, totalH, totalArea: totalW * totalH,
        projCount: count, spacingH, spacingV, overlapHPct, overlapVPct,
        overlapHm: overlap.h, overlapVm: overlap.v,
        combinedResW: isH ? resW + (resW - Math.round(resW * overlapHPct / 100)) * (count - 1) : resW,
        combinedResH: isH ? resH : resH + (resH - Math.round(resH * overlapVPct / 100)) * (count - 1),
        ppi: Math.round(resW / (img.w * 39.3701)),
        perProjW: img.w, perProjH: img.h
      };
    } else if (mode === MODE_GRID) {
      const totalW = img.w + spacingH * (gridCols - 1);
      const totalH = img.h + spacingV * (gridRows - 1);
      return {
        totalW, totalH, totalArea: totalW * totalH,
        projCount: gridRows * gridCols, spacingH, spacingV, overlapHPct, overlapVPct,
        overlapHm: overlap.h, overlapVm: overlap.v,
        combinedResW: resW + (resW - Math.round(resW * overlapHPct / 100)) * (gridCols - 1),
        combinedResH: resH + (resH - Math.round(resH * overlapVPct / 100)) * (gridRows - 1),
        ppi: Math.round(resW / (img.w * 39.3701)),
        perProjW: img.w, perProjH: img.h
      };
    } else {
      return {
        totalW: domeRadius * 2, totalH: img.h, totalArea: Math.PI * domeRadius * domeRadius,
        projCount: domeCount, spacingH: 0, spacingV: 0, overlapHPct: 0, overlapVPct: 0,
        overlapHm: 0, overlapVm: 0,
        combinedResW: resW * domeCount, combinedResH: resH,
        ppi: Math.round(resW / (img.w * 39.3701)),
        perProjW: img.w, perProjH: img.h
      };
    }
  }, [baseProjector, mode, count, direction, overlapMode, overlapH, overlapV, overlapDistH, overlapDistV, unit, gridRows, gridCols, domeAngle, domeCount, domeRadius, calcImageSize, getOverlapMeters]);

  const handleGenerate = useCallback(() => {
    if (!baseProjector) { toast.error('Select a projector first'); return; }

    const img = calcImageSize(baseProjector);
    const overlap = getOverlapMeters(img.w, img.h);
    const spacingH = img.w - overlap.h;
    const spacingV = img.h - overlap.v;
    const basePos = baseProjector.position;
    const baseRot = baseProjector.rotation;
    const axes = getProjectorAxes(baseProjector);

    saveToHistoryImmediate();

    // Build all new projector position offsets
    const offsets = [];
    if (mode === MODE_LINEAR) {
      const dirVectors = {
        right: { x: axes.right.x, y: axes.right.y, z: axes.right.z },
        left:  { x: -axes.right.x, y: -axes.right.y, z: -axes.right.z },
        top:   { x: axes.up.x, y: axes.up.y, z: axes.up.z },
        bottom:{ x: -axes.up.x, y: -axes.up.y, z: -axes.up.z }
      };
      const dir = dirVectors[direction];
      const isH = direction === 'left' || direction === 'right';
      const spacing = isH ? spacingH : spacingV;
      for (let i = 1; i < count; i++) {
        offsets.push({
          pos: { x: dir.x * spacing * i, y: dir.y * spacing * i, z: dir.z * spacing * i },
          rot: { ...baseRot }
        });
      }
    } else if (mode === MODE_GRID) {
      for (let r = 0; r < gridRows; r++) {
        for (let c = 0; c < gridCols; c++) {
          if (r === 0 && c === 0) continue;
          offsets.push({
            pos: {
              x: axes.right.x * c * spacingH + axes.up.x * (-r * spacingV),
              y: axes.right.y * c * spacingH + axes.up.y * (-r * spacingV),
              z: axes.right.z * c * spacingH + axes.up.z * (-r * spacingV)
            },
            rot: { ...baseRot }
          });
        }
      }
    } else if (mode === MODE_DOME) {
      const angleStep = (domeAngle / domeCount) * (Math.PI / 180);
      const startAngle = -(domeAngle / 2) * (Math.PI / 180);
      for (let i = 1; i < domeCount; i++) {
        const angle = startAngle + angleStep * i;
        offsets.push({
          pos: {
            x: Math.sin(angle) * domeRadius - Math.sin(startAngle) * domeRadius,
            y: 0,
            z: Math.cos(angle) * domeRadius - Math.cos(startAngle) * domeRadius
          },
          rot: {
            x: baseRot.x,
            y: baseRot.y + (angle - startAngle) * (180 / Math.PI),
            z: baseRot.z
          }
        });
      }
    }

    // Build complete projector objects with correct positions from the start
    const defaultLens = baseProjector.selected_lens || baseProjector.spec?.lenses?.[0] || null;
    const newProjectors = offsets.map((offset, idx) => {
      const namePrefix = baseProjector.is_reverse_projector ? 'RPR' : 'PR';
      return {
        id: crypto.randomUUID(),
        spec_id: baseProjector.spec_id,
        name: `${namePrefix}-${projectors.length + idx + 1}`,
        model: baseProjector.model,
        is_reverse_projector: baseProjector.is_reverse_projector || false,
        position: {
          x: basePos.x + offset.pos.x,
          y: basePos.y + offset.pos.y,
          z: basePos.z + offset.pos.z
        },
        rotation: offset.rot,
        lensType: baseProjector.lensType || 'standard',
        lens90Rotation: baseProjector.lens90Rotation || 'right',
        topBottomDirection: baseProjector.topBottomDirection || 'top',
        lens_shift_h: baseProjector.lens_shift_h || 0,
        lens_shift_v: baseProjector.lens_shift_v || 0,
        brightness: baseProjector.brightness || 100,
        throw_ratio: baseProjector.throw_ratio || 1.5,
        throw_distance: baseProjector.throw_distance || 5,
        show_beam: true,
        beamOpacity: baseProjector.beamOpacity || 0.2,
        show_grid: baseProjector.show_grid || false,
        show_border: baseProjector.show_border ?? true,
        show_frame: baseProjector.show_frame || false,
        show_clamp: baseProjector.show_clamp || false,
        color: baseProjector.color || '#878787',
        body_color: baseProjector.body_color || '#2a2a2a',
        body_width: baseProjector.body_width || 500,
        body_height: baseProjector.body_height || 200,
        body_depth: baseProjector.body_depth || 600,
        weight: baseProjector.weight || 20,
        spec: baseProjector.spec,
        aspect_ratio: baseProjector.aspect_ratio || '16:10',
        image_width: baseProjector.image_width || 1920,
        image_height: baseProjector.image_height || 1200,
        lock_aspect_ratio: true,
        lock_image_size: false,
        projector_image: baseProjector.projector_image || null,
        power_on: true,
        shadow_enabled: true,
        test_pattern_on: baseProjector.test_pattern_on || false,
        test_pattern_id: baseProjector.test_pattern_id || 'grid',
        selected_lens_id: baseProjector.selected_lens_id || null,
        selected_lens: defaultLens,
        custom_lenses: [],
        out_of_range: false,
        lumens: baseProjector.lumens || baseProjector.spec?.brightness || 10000,
        show_name: true,
        locked: false
      };
    });

    // Add all projectors at once with correct positions
    setProjectors(prev => [...prev, ...newProjectors]);
    const newIds = newProjectors.map(p => p.id);
    setGeneratedIds(newIds);
    toast.success(`Created ${newIds.length} projectors in ${mode} layout`);
  }, [baseProjector, mode, count, direction, overlapMode, overlapH, overlapV, overlapDistH, overlapDistV, unit, gridRows, gridCols, domeAngle, domeCount, domeRadius, projectors.length, setProjectors, saveToHistoryImmediate, calcImageSize, getOverlapMeters, getProjectorAxes]);

  const fmtDist = (m) => decimals === 0 ? Math.round(metersToUnit(m, unit)) : metersToUnit(m, unit).toFixed(decimals);

  if (!baseProjector) {
    return (
      <div className="p-4 text-center text-zinc-500 text-xs">
        <Copy className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p>Select a projector to use as the base reference for multi-projection layout.</p>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-4 text-xs overflow-y-auto" data-testid="multi-projection-panel">
      <div className="text-zinc-400 text-[10px]">Base: {baseProjector.name} ({baseProjector.model})</div>

      {/* Mode Selector */}
      <div className="grid grid-cols-3 gap-1">
        {[
          { id: MODE_LINEAR, icon: <ChevronRight className="w-3 h-3" />, label: 'Linear' },
          { id: MODE_GRID, icon: <Grid3X3 className="w-3 h-3" />, label: 'Grid' },
          { id: MODE_DOME, icon: <Circle className="w-3 h-3" />, label: 'Dome' }
        ].map(m => (
          <Button key={m.id} variant={mode === m.id ? 'default' : 'outline'} size="sm"
            className={`h-8 text-xs gap-1 ${mode === m.id ? 'bg-fuchsia-600 hover:bg-fuchsia-700' : 'border-zinc-700'}`}
            onClick={() => setMode(m.id)} data-testid={`mode-${m.id}`}>
            {m.icon}{m.label}
          </Button>
        ))}
      </div>

      {/* Overlap Mode Toggle (for Linear & Grid) */}
      {mode !== MODE_DOME && (
        <div>
          <Label className="text-[10px] text-zinc-400">Overlap Input Mode</Label>
          <div className="grid grid-cols-2 gap-1 mt-1">
            <Button variant={overlapMode === 'percent' ? 'default' : 'outline'} size="sm"
              className={`h-7 text-[10px] ${overlapMode === 'percent' ? 'bg-zinc-600' : 'border-zinc-700'}`}
              onClick={() => setOverlapMode('percent')}>
              Percentage (%)
            </Button>
            <Button variant={overlapMode === 'distance' ? 'default' : 'outline'} size="sm"
              className={`h-7 text-[10px] ${overlapMode === 'distance' ? 'bg-zinc-600' : 'border-zinc-700'}`}
              onClick={() => setOverlapMode('distance')}>
              Distance ({unit})
            </Button>
          </div>
        </div>
      )}

      {/* LINEAR Controls */}
      {mode === MODE_LINEAR && (
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] text-zinc-400">Projector Count</Label>
            <div className="flex items-center gap-2 mt-1">
              <Slider value={[count]} min={2} max={20} step={1} onValueChange={([v]) => setCount(v)} className="flex-1" />
              <Input type="number" value={count} min={2} max={20}
                onChange={e => setCount(Math.max(2, Math.min(20, parseInt(e.target.value) || 2)))}
                className="w-14 h-7 text-xs bg-zinc-900 border-zinc-700 text-right" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] text-zinc-400">Direction</Label>
            <div className="grid grid-cols-4 gap-1 mt-1">
              {[
                { d: 'left', icon: <ChevronLeft className="w-3 h-3" /> },
                { d: 'right', icon: <ChevronRight className="w-3 h-3" /> },
                { d: 'top', icon: <ChevronUp className="w-3 h-3" /> },
                { d: 'bottom', icon: <ChevronDown className="w-3 h-3" /> }
              ].map(({ d, icon }) => (
                <Button key={d} variant={direction === d ? 'default' : 'outline'} size="sm"
                  className={`h-7 text-[10px] gap-0.5 ${direction === d ? 'bg-zinc-600' : 'border-zinc-700'}`}
                  onClick={() => setDirection(d)}>
                  {icon}{d.charAt(0).toUpperCase() + d.slice(1)}
                </Button>
              ))}
            </div>
          </div>
          {overlapMode === 'percent' ? (
            <div>
              <Label className="text-[10px] text-zinc-400">Overlap ({overlapH}%)</Label>
              <Slider value={[overlapH]} min={0} max={50} step={1} onValueChange={([v]) => setOverlapH(v)} className="mt-1" />
            </div>
          ) : (
            <div>
              <Label className="text-[10px] text-zinc-400">Overlap Distance ({unit})</Label>
              <Input type="number" step={decimals === 0 ? 1 : 0.01}
                defaultValue={overlapDistH} key={`odh-${unit}`}
                onBlur={e => { const v = parseFloat(e.target.value); if (!isNaN(v) && v >= 0) setOverlapDistH(v); }}
                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                className="h-7 text-xs bg-zinc-900 border-zinc-700 mt-1" />
            </div>
          )}
        </div>
      )}

      {/* GRID Controls */}
      {mode === MODE_GRID && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-zinc-400">Columns</Label>
              <Input type="number" value={gridCols} min={1} max={10}
                onChange={e => setGridCols(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                className="h-7 text-xs bg-zinc-900 border-zinc-700 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-400">Rows</Label>
              <Input type="number" value={gridRows} min={1} max={10}
                onChange={e => setGridRows(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                className="h-7 text-xs bg-zinc-900 border-zinc-700 mt-1" />
            </div>
          </div>
          {overlapMode === 'percent' ? (
            <>
              <div>
                <Label className="text-[10px] text-zinc-400">Horizontal Overlap ({overlapH}%)</Label>
                <Slider value={[overlapH]} min={0} max={50} step={1} onValueChange={([v]) => setOverlapH(v)} className="mt-1" />
              </div>
              <div>
                <Label className="text-[10px] text-zinc-400">Vertical Overlap ({overlapV}%)</Label>
                <Slider value={[overlapV]} min={0} max={50} step={1} onValueChange={([v]) => setOverlapV(v)} className="mt-1" />
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-zinc-400">H Overlap ({unit})</Label>
                <Input type="number" step={decimals === 0 ? 1 : 0.01}
                  defaultValue={overlapDistH} key={`odh-${unit}`}
                  onBlur={e => { const v = parseFloat(e.target.value); if (!isNaN(v) && v >= 0) setOverlapDistH(v); }}
                  onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                  className="h-7 text-xs bg-zinc-900 border-zinc-700 mt-1" />
              </div>
              <div>
                <Label className="text-[10px] text-zinc-400">V Overlap ({unit})</Label>
                <Input type="number" step={decimals === 0 ? 1 : 0.01}
                  defaultValue={overlapDistV} key={`odv-${unit}`}
                  onBlur={e => { const v = parseFloat(e.target.value); if (!isNaN(v) && v >= 0) setOverlapDistV(v); }}
                  onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                  className="h-7 text-xs bg-zinc-900 border-zinc-700 mt-1" />
              </div>
            </div>
          )}
          <div className="text-zinc-500 text-[10px]">Total: {gridRows} x {gridCols} = {gridRows * gridCols} projectors</div>
        </div>
      )}

      {/* DOME Controls */}
      {mode === MODE_DOME && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-zinc-400">Projectors</Label>
              <Input type="number" value={domeCount} min={2} max={24}
                onChange={e => setDomeCount(Math.max(2, Math.min(24, parseInt(e.target.value) || 2)))}
                className="h-7 text-xs bg-zinc-900 border-zinc-700 mt-1" />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-400">Angle (total)</Label>
              <Input type="number" value={domeAngle} min={30} max={360} step={10}
                onChange={e => setDomeAngle(Math.max(30, Math.min(360, parseInt(e.target.value) || 180)))}
                className="h-7 text-xs bg-zinc-900 border-zinc-700 mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] text-zinc-400">Radius ({fmtDist(domeRadius)} {unit})</Label>
            <Slider value={[domeRadius]} min={2} max={30} step={0.5} onValueChange={([v]) => setDomeRadius(v)} className="mt-1" />
          </div>
          <div className="text-zinc-500 text-[10px]">
            {domeAngle}° / {domeCount} = {(domeAngle / domeCount).toFixed(1)}° per projector
          </div>
        </div>
      )}

      {/* Generate Button */}
      <Button onClick={handleGenerate} className="w-full h-9 bg-fuchsia-600 hover:bg-fuchsia-700 text-xs font-medium"
        data-testid="generate-multi-projection">
        <Plus className="w-4 h-4 mr-1" />
        Generate {mode === MODE_LINEAR ? count : mode === MODE_GRID ? gridRows * gridCols : domeCount} Projectors
      </Button>

      {generatedIds.length > 0 && (
        <div className="text-[10px] text-green-400">
          {generatedIds.length} projectors created
        </div>
      )}

      {/* Output Stats */}
      {stats && (
        <div className="space-y-2 pt-2 border-t border-zinc-800">
          <div className="text-[10px] text-zinc-400 font-medium">Coverage Summary</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
            <span className="text-zinc-500">Total Width</span>
            <span className="font-mono text-right">{fmtDist(stats.totalW)} {unit}</span>
            <span className="text-zinc-500">Total Height</span>
            <span className="font-mono text-right">{fmtDist(stats.totalH)} {unit}</span>
            <span className="text-zinc-500">Total Area</span>
            <span className="font-mono text-right">{stats.totalArea.toFixed(1)} m2</span>
            <span className="text-zinc-500">Projectors</span>
            <span className="font-mono text-right">{stats.projCount}</span>
            {stats.spacingH > 0 && <>
              <span className="text-zinc-500">Spacing (H)</span>
              <span className="font-mono text-right">{fmtDist(stats.spacingH)} {unit}</span>
            </>}
            {stats.spacingV > 0 && <>
              <span className="text-zinc-500">Spacing (V)</span>
              <span className="font-mono text-right">{fmtDist(stats.spacingV)} {unit}</span>
            </>}
          </div>

          <div className="text-[10px] text-zinc-400 font-medium mt-2">Overlap</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
            <span className="text-zinc-500">Horizontal</span>
            <span className="font-mono text-right">{stats.overlapHPct}% ({fmtDist(stats.overlapHm)} {unit})</span>
            <span className="text-zinc-500">Vertical</span>
            <span className="font-mono text-right">{stats.overlapVPct}% ({fmtDist(stats.overlapVm)} {unit})</span>
          </div>

          <div className="text-[10px] text-zinc-400 font-medium mt-2">Resolution</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
            <span className="text-zinc-500">Per Projector</span>
            <span className="font-mono text-right">{fmtDist(stats.perProjW)} x {fmtDist(stats.perProjH)} {unit}</span>
            <span className="text-zinc-500">Combined Res</span>
            <span className="font-mono text-right">{stats.combinedResW} x {stats.combinedResH} px</span>
            <span className="text-zinc-500">PPI</span>
            <span className="font-mono text-right">{stats.ppi}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiProjectionPanel;
